/*
  停止のページ (/unsubscribe) と同意の確認のページ (/mail-consent) の住所には、?t= にメールの印が入る。
  印は暗号化していてメールの住所は読めないが、印そのものが「止める」「受け取りを確定する」の権限になる。
  Sentry などの外へ送る記録に残さないよう、この 2 つのページの住所は問い合わせと断片を落とす
*/

const MAIL_TOKEN_PAGE = /\/(unsubscribe|mail-consent)(\.data)?\/?$/i;

/** 印の入るページの住所なら、問い合わせと断片を落とした住所を返す。ほかの住所と、住所でない文字列はそのまま */
export function redactMailTokenUrl(raw: string): string {
  let url: URL;
  try {
    url = new URL(raw, "https://placeholder.invalid");
  } catch {
    return raw;
  }
  if (!MAIL_TOKEN_PAGE.test(url.pathname) || (!url.search && !url.hash)) return raw;
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(raw);
  return absolute ? `${url.origin}${url.pathname}` : url.pathname;
}

/** 問い合わせの鍵をページ (URLSearchParams) と同じ読み方でデコードする。%74 は t、+ は空白。壊れた % はそのまま */
function decodeQueryKey(key: string): string {
  try {
    return decodeURIComponent(key.replace(/\+/g, " "));
  } catch {
    return key;
  }
}

/** 鍵が印の鍵 (t か token) か。%74 や to%6ben のようにエンコードした鍵も、デコードしてから見る */
function isMailTokenKey(key: string): boolean {
  return /^(t|token)$/i.test(decodeQueryKey(key));
}

/**
 * 問い合わせの文字列 (http.query、url.query、query_string) の t= の値を伏せる。
 * 問い合わせだけではどのページか分からないので、ページを見ずに t= を伏せる (ほかのページの t= も伏せてよい)。
 * 鍵はデコードしてから見る (%74=<印> もページでは t として読まれる)
 */
export function redactMailTokenQuery(query: string): string {
  return query.replace(/(^\??|[&;])([^=&;#]*)=([^&;#]*)/g, (whole, lead: string, key: string) =>
    isMailTokenKey(key) ? `${lead}${key}=[Filtered]` : whole,
  );
}

type Bag = Record<string, unknown> | undefined;

const URL_KEYS = ["url", "http.url", "url.full"] as const;
const QUERY_KEYS = ["http.query", "url.query"] as const;

function redactIn(bag: Bag, keys: readonly string[], redact: (value: string) => string = redactMailTokenUrl): void {
  if (!bag) return;
  for (const key of keys) {
    const value = bag[key];
    if (typeof value === "string") bag[key] = redact(value);
  }
}

function redactSpanData(data: Bag): void {
  redactIn(data, URL_KEYS);
  redactIn(data, QUERY_KEYS, redactMailTokenQuery);
}

/** Sentry の request.query_string は文字列、[名前, 値] の並び、名前と値の組のどれか */
function redactQueryString(value: unknown): unknown {
  if (typeof value === "string") return redactMailTokenQuery(value);
  if (Array.isArray(value)) {
    return value.map((pair) =>
      Array.isArray(pair) && typeof pair[0] === "string" && isMailTokenKey(pair[0]) ? [pair[0], "[Filtered]"] : pair,
    );
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const key of Object.keys(out)) if (isMailTokenKey(key)) out[key] = "[Filtered]";
    return out;
  }
  return value;
}

type SentryLikeEvent = {
  request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
  transaction?: string;
  breadcrumbs?: { data?: Record<string, unknown> }[];
  spans?: { description?: string; data?: Record<string, unknown> }[];
  contexts?: { trace?: { data?: Record<string, unknown> } };
};

/** Sentry の beforeSend と beforeSendTransaction に渡す。印の入るページの住所を落としたイベントを返す */
export function redactMailTokensInSentryEvent<T extends SentryLikeEvent>(event: T): T {
  const request = event.request;
  if (request?.url && redactMailTokenUrl(request.url) !== request.url) {
    request.url = redactMailTokenUrl(request.url);
    delete request.query_string;
  }
  if (request && request.query_string !== undefined) request.query_string = redactQueryString(request.query_string);
  redactIn(request?.headers, ["Referer", "referer"]);
  if (typeof event.transaction === "string") event.transaction = redactMailTokenUrl(event.transaction);
  for (const crumb of event.breadcrumbs ?? []) redactIn(crumb.data, ["url", "from", "to"]);
  for (const span of event.spans ?? []) {
    if (typeof span.description === "string") span.description = span.description.replace(/\S+/g, (w) => redactMailTokenUrl(w));
    redactSpanData(span.data);
  }
  redactSpanData(event.contexts?.trace?.data);
  return event;
}

/** Sentry の beforeBreadcrumb に渡す */
export function redactMailTokensInBreadcrumb<T extends { data?: Record<string, unknown> }>(crumb: T): T {
  redactIn(crumb.data, ["url", "from", "to"]);
  redactIn(crumb.data, QUERY_KEYS, redactMailTokenQuery);
  return crumb;
}
